import { DomHelper, AnswerExtractor } from '../../utils';
import { ValidationService, ErrorService } from '../core';
import type { SurveyStateManager } from '../survey';

export class EventManager {
  private stateManager: SurveyStateManager;
  private onRender: () => void;
  private onSubmit: () => Promise<void>;

  constructor(
    stateManager: SurveyStateManager, 
    onRender: () => void,
    onSubmit: () => Promise<void>
  ) {
    this.stateManager = stateManager;
    this.onRender = onRender;
    this.onSubmit = onSubmit;
  }

  attachIntroListeners(): void {
    const startBtn = DomHelper.getElement('#start-survey-btn');
    startBtn?.addEventListener('click', () => {
      this.stateManager.setShowingIntro(false);
      this.onRender();
    });
  }

  attachNavigationListeners(): void {
    const prevBtn = DomHelper.getElement('#prev-btn');
    const nextBtn = DomHelper.getElement('#next-btn');
    const submitBtn = DomHelper.getElement('#submit-btn');

    prevBtn?.addEventListener('click', () => {
      if (this.stateManager.previousQuestion()) {
        this.onRender();
      }
    });

    nextBtn?.addEventListener('click', () => {
      if (this.stateManager.nextQuestion()) {
        this.onRender();
      }
    });

    submitBtn?.addEventListener('click', async () => {
      const question = this.stateManager.getCurrentQuestion();
      // Get current answer from DOM, fallback to saved answer
      const currentAnswer = AnswerExtractor.extractAnswer(question.type) ?? this.stateManager.getAnswers()[question.id];
      const validation = ValidationService.validateQuestion(question, currentAnswer);
      
      if (!validation.isValid) {
        ErrorService.showError(validation.message!);
        return;
      }

      ErrorService.clearError();
      
      // Save the current answer before submission
      this.stateManager.saveCurrentAnswerForSubmission();
      
      // Show loading state on submit button
      this.setSubmitButtonLoading(true);
      
      try {
        await this.onSubmit();
      } catch (error) {
        this.setSubmitButtonLoading(false);
        throw error;
      }
    });
  }

  attachRatingListeners(): void {
    const stars = DomHelper.getElements('.star');
    stars.forEach(star => {
      star.addEventListener('click', (e) => {
        const rating = (e.target as HTMLElement).getAttribute('data-rating');
        this.handleRatingClick(rating, stars);
      });
    });
  }

  private handleRatingClick(rating: string | null, stars: NodeListOf<Element>): void {
    const hiddenInput = DomHelper.getElement<HTMLInputElement>('#answer');
    if (hiddenInput && rating) {
      hiddenInput.value = rating;
      this.updateStarVisuals(stars, parseInt(rating));
    }
  }

  private updateStarVisuals(stars: NodeListOf<Element>, selectedRating: number): void {
    stars.forEach((star, index) => {
      star.classList.toggle('selected', index < selectedRating);
    });
  }

  private setSubmitButtonLoading(isLoading: boolean): void {
    const submitBtn = DomHelper.getElement<HTMLButtonElement>('#submit-btn');
    if (!submitBtn) return;

    if (isLoading) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span style="display: inline-flex; align-items: center; gap: 8px;"><div style="width: 16px; height: 16px; border: 2px solid #fff; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>Submitting...</span>';
      submitBtn.style.cursor = 'not-allowed';
      submitBtn.style.opacity = '0.7';
    } else {
      submitBtn.disabled = false;
      submitBtn.innerHTML = 'Submit';
      submitBtn.style.cursor = 'pointer';
      submitBtn.style.opacity = '1';
    }
  }

  attachAllListeners(): void {
    this.attachNavigationListeners();
    this.attachRatingListeners();
  }
}